import { useEffect, useId, useState } from 'react';
import { clsx } from '@mentimeter/ragnar-tailwind-config';
import type { ColorsEnumOnly } from '@mentimeter/ragnar-colors';
import { isThemeColor } from '@mentimeter/ragnar-dsc';
import { useRagnar } from '@mentimeter/ragnar-react';

export interface AnimatedIllustrationWrapperProps {
  src: string;
  type: 'contextual' | 'spotlight';
  'aria-label': string;
  animationMode?: 'auto' | 'onHover' | undefined;
  color?: ColorsEnumOnly | 'currentColor' | undefined;
  size?: string | undefined;
}

export const AnimatedIllustrationWrapper = ({
  src,
  type,
  'aria-label': ariaLabel,
  animationMode = 'auto',
  color = 'text',
  size,
}: AnimatedIllustrationWrapperProps) => {
  const [stillUrl, setStillUrl] = useState<string>();
  const [url, setUrl] = useState<string>();
  const maskId = useId();
  const { theme } = useRagnar();

  const colorValue = isThemeColor(theme, color) ? theme.colors[color] : color;

  useEffect(() => {
    if (animationMode === 'onHover') {
      // get + set still image
      const img = document.createElement('img');
      img.crossOrigin = 'Anonymous';
      img.src = src;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);
        const canvasUrl = canvas.toDataURL('image/webp');
        setStillUrl(canvasUrl);
        setUrl(canvasUrl);
      };
    } else {
      // set animated image
      setUrl(src);
    }
  }, [animationMode, src]);

  const showAnimated = () => {
    if (animationMode === 'onHover') setUrl(src);
  };
  const showStill = () => {
    if (animationMode === 'onHover') setUrl(stillUrl);
  };

  return (
    <div
      className={clsx(
        'relative',
        'aspect-square',
        type === 'spotlight'
          ? 'max-h-106 min-h-36 h-68'
          : 'max-h-50 min-h-18 h-35',
        size && 'h-[var(--animated-illustration-height)]',
      )}
      style={
        {
          '--animated-illustration-height': size,
        } as React.CSSProperties
      }
      onMouseEnter={showAnimated}
      onMouseLeave={showStill}
    >
      <svg width="100%" height="100%" aria-label={ariaLabel}>
        {/* eslint-disable-next-line svg-jsx/camel-case-dash */}
        <mask id={maskId} maskUnits="userSpaceOnUse" mask-type="alpha">
          <image
            href={url}
            width="100%"
            height="100%"
            preserveAspectRatio="none"
          />
        </mask>
      </svg>

      <div
        className={clsx('absolute', 'inset-0')}
        style={{ maskImage: `url(#${maskId})`, background: colorValue }}
      />
    </div>
  );
};
